import React from 'react';
import { Mui } from '@leap4/frontend';
import { ITrackedLine, LineStatus } from './appAdsLines.interface';

const { Chip, Tooltip } = Mui;

/**
 * The four line states, as a chip.
 *
 * "Wrong type" shows what the file actually says alongside what we expect, because those are
 * different asks to the publisher: a missing line needs adding, a wrong one needs correcting.
 * "Unverified" is a real state, not an absence - it means nobody has looked at the file yet,
 * which is not the same as "fine".
 */
export const StatusChip: React.FC<{ line: ITrackedLine }> = ({ line }) => {
    const status: LineStatus = line?.status ?? null;

    if (status === 'correct') {
        return <Chip size="small" color="success" variant="outlined" label="live & correct" />;
    }

    if (status === 'incorrect') {
        return (
            <Tooltip title={`Expected ${line.relationship}, live file has ${line.actualRelationship || '?'}`}>
                <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`wrong type (live: ${line.actualRelationship || '?'})`}
                />
            </Tooltip>
        );
    }

    if (status === 'missing') {
        return <Chip size="small" color="error" variant="outlined" label="not found" />;
    }

    return (
        <Tooltip title="This line has never been checked against the publisher's live file.">
            <Chip size="small" color="default" variant="outlined" label="unverified" />
        </Tooltip>
    );
};

/**
 * Whether a removed line is still live, or unknown.
 *
 * The unknown case is deliberately loud rather than silently treated as resolved: if the last
 * check predates the removal, we genuinely don't know whether the publisher has taken the line
 * out, and telling ad-ops either way would be a guess.
 */
export const RemovalStatusChip: React.FC<{ line: ITrackedLine }> = ({ line }) => {
    if (line.needsRecheck) {
        return (
            <Tooltip title="No check has run since this line was removed, so we don't know whether it's still live. Run a check to find out.">
                <Chip size="small" color="info" variant="outlined" label="not checked since removal" />
            </Tooltip>
        );
    }

    if (line.foundInFile === true) {
        return (
            <Tooltip title="Still present in the publisher's live file - needs an update request.">
                <Chip size="small" color="error" variant="outlined" label="still live" />
            </Tooltip>
        );
    }

    if (line.foundInFile === false) {
        return <Chip size="small" color="success" variant="outlined" label="confirmed removed" />;
    }

    return <Chip size="small" color="default" variant="outlined" label="unknown" />;
};
